import React from "react";
import {
    LayoutDashboard,
    Boxes,
    LineChart,
    Warehouse,
    Users,
    Truck,
    Wallet,
    BarChart3,
    Sparkles,
    ShoppingBag,
    Receipt,
    ShoppingCart,
    UserCog,
    ScrollText,
    Settings as SettingsIcon,
    LifeBuoy,
    Search,
    ChevronsUpDown,
    ChevronRight,
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    CheckCircle2,
    ArrowUpRight,
} from "lucide-react";

/**
 * BrowserFrame — realistic browser chrome around any product preview.
 * Renders traffic-light dots, address bar, and shadowed container.
 */
export function BrowserFrame({ url = "yugos.app", children, className = "", tone = "light" }) {
    const isDark = tone === "dark";
    return (
        <div
            className={`overflow-hidden rounded-2xl border ${
                isDark ? "border-zinc-800 bg-zinc-950" : "border-[#e4dccc] bg-white"
            } ${className}`}
            data-testid="browser-frame"
        >
            <div
                className={`flex items-center gap-2 border-b px-3 py-2.5 ${
                    isDark ? "border-zinc-800 bg-zinc-900" : "border-[#ece5d6] bg-[#f4efe5]"
                }`}
            >
                <div className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
                </div>
                <div
                    className={`ml-3 flex-1 max-w-md mx-auto rounded-md px-3 py-1 text-[11px] font-mono ${
                        isDark ? "bg-zinc-800 text-zinc-400" : "bg-white text-stone-500 border border-[#ece5d6]"
                    }`}
                >
                    {url}
                </div>
                <div className="w-12" />
            </div>
            <div className={isDark ? "bg-zinc-950" : "bg-white"}>{children}</div>
        </div>
    );
}

/** MiniSidebar — condensed version of the real app sidebar for previews. */
function MiniSidebar({ active = "dashboard" }) {
    const items = [
        { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
        { id: "product-brain", label: "Product Brain", icon: Boxes },
        { id: "inventory-intelligence", label: "Inventory", icon: LineChart },
        { id: "warehouse-hub", label: "Warehouse Hub", icon: Warehouse },
        { id: "customer-brain", label: "Customer Brain", icon: Users },
        { id: "supplier-brain", label: "Supplier Brain", icon: Truck },
        { id: "yug-brain", label: "YugOS Intelligence", icon: Sparkles, ai: true },
        { id: "sales-hub", label: "Sales Hub", icon: ShoppingBag },
        { id: "billing", label: "Billing", icon: Receipt },
        { id: "finance-hub", label: "Finance Hub", icon: Wallet },
        { id: "business-intelligence", label: "BI", icon: BarChart3 },
        { id: "procurement", label: "Procurement", icon: ShoppingCart },
        { id: "user-management", label: "Users", icon: UserCog },
        { id: "audit-logs", label: "Audit Logs", icon: ScrollText },
        { id: "settings", label: "Settings", icon: SettingsIcon },
        { id: "help", label: "Help", icon: LifeBuoy },
    ];
    return (
        <aside className="hidden md:flex w-44 shrink-0 flex-col border-r border-zinc-200 bg-zinc-50/60 py-3 px-2">
            <div className="flex items-center gap-2 px-2 pb-3 border-b border-zinc-200 mb-3">
                <img
                    src="/brand/yugos-logo.png"
                    alt="YugOS"
                    width={22}
                    height={22}
                    className="shrink-0 select-none"
                    draggable={false}
                />
                <span className="font-display font-semibold text-xs tracking-tight">YugOS</span>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2 py-1.5 mb-3">
                <img
                    src="/brand/yugos-logo.png"
                    alt=""
                    width={18}
                    height={18}
                    className="shrink-0 select-none"
                    draggable={false}
                    aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-medium truncate">Yug Traders</div>
                </div>
                <ChevronsUpDown className="h-3 w-3 text-zinc-400" strokeWidth={1.5} />
            </div>
            <nav className="flex-1 space-y-0.5">
                {items.map((it) => (
                    <div
                        key={it.id}
                        className={`flex items-center gap-2 rounded-md px-2 py-1 text-[10.5px] ${
                            active === it.id ? "bg-zinc-900 text-white" : "text-zinc-700"
                        }`}
                    >
                        <it.icon className={`h-3 w-3 ${it.ai ? "text-blue-500" : ""}`} strokeWidth={1.5} />
                        <span className="truncate">{it.label}</span>
                        {it.ai && (
                            <span className="ml-auto text-[7px] font-medium tracking-wider uppercase text-blue-500">
                                AI
                            </span>
                        )}
                    </div>
                ))}
            </nav>
        </aside>
    );
}

/** MiniTopbar — matches real topbar. */
function MiniTopbar({ crumb }) {
    return (
        <header className="flex h-9 items-center gap-3 border-b border-zinc-200 bg-white/80 px-4 shrink-0">
            <div className="flex items-center gap-1 text-[10.5px] text-zinc-500">
                <span>Yug Traders</span>
                <ChevronRight className="h-3 w-3 text-zinc-300" strokeWidth={1.5} />
                <span className="text-zinc-900 font-medium">{crumb}</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 text-[10px] text-zinc-500">
                    <Search className="h-2.5 w-2.5" strokeWidth={1.5} />
                    <span>Search anything…</span>
                    <span className="font-mono text-[9px] text-zinc-400">⌘K</span>
                </div>
                <div className="flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-1 py-0.5">
                    <div className="flex h-4 w-4 items-center justify-center rounded-full bg-zinc-900 text-white text-[8px] font-semibold">
                        AK
                    </div>
                    <div className="pr-1 hidden md:block">
                        <div className="text-[9px] font-medium">Aditya Kumar</div>
                    </div>
                </div>
            </div>
        </header>
    );
}

/** Shell — wraps a preview with sidebar + topbar. */
function Shell({ crumb, active, children }) {
    return (
        <div className="flex h-[380px] sm:h-[440px] bg-zinc-50 text-zinc-950 text-[11px] leading-tight" data-testid={`preview-${active}`}>
            <MiniSidebar active={active} />
            <div className="flex flex-1 flex-col overflow-hidden min-w-0">
                <MiniTopbar crumb={crumb} />
                <main className="flex-1 overflow-hidden p-3 sm:p-4">{children}</main>
            </div>
        </div>
    );
}

/**
 * ScaledFrame — renders a fixed-width "desktop design" and proportionally scales
 * it down to fit narrow viewports. Keeps every pixel of the preview readable
 * on mobile without redesigning each shell for a phone.
 *
 * Guarantees: content is scaled to fully fit the available width. No floor,
 * no clipping — the design will shrink as small as required. Consumers get
 * the *rendered* height back via `onHeight` if they need to layout around it.
 */
export function ScaledFrame({ children, designWidth = 820, designHeight = 500, maxScale = 1 }) {
    const wrapRef = React.useRef(null);
    const [scale, setScale] = React.useState(maxScale);
    React.useLayoutEffect(() => {
        const update = () => {
            if (!wrapRef.current) return;
            const w = wrapRef.current.getBoundingClientRect().width;
            // No floor — always fit. Cap at maxScale so we never upscale.
            const next = Math.min(maxScale, w / designWidth);
            setScale(next > 0 ? next : 0.001);
        };
        update();
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
        if (ro && wrapRef.current) ro.observe(wrapRef.current);
        window.addEventListener("resize", update);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [designWidth, maxScale]);
    return (
        <div
            ref={wrapRef}
            className="w-full overflow-hidden"
            style={{ height: designHeight * scale }}
        >
            <div
                style={{
                    width: designWidth,
                    height: designHeight,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                }}
            >
                {children}
            </div>
        </div>
    );
}

/* --------------------------- Sample module previews --------------------------- */

function KPI({ label, value, delta, positive = true }) {
    return (
        <div className="rounded-md border border-zinc-200 bg-white p-2.5">
            <div className="text-[9px] font-medium uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="mt-1 font-display text-base font-semibold tracking-tight">{value}</div>
            {delta && (
                <div className={`mt-0.5 flex items-center gap-1 text-[9px] ${positive ? "text-emerald-600" : "text-red-600"}`}>
                    {positive ? <TrendingUp className="h-2.5 w-2.5" strokeWidth={1.5} /> : <TrendingDown className="h-2.5 w-2.5" strokeWidth={1.5} />}
                    {delta}
                </div>
            )}
        </div>
    );
}

export function DashboardPreview() {
    // Realistic Yug Traders numbers — wholesale trading, ~₹42L/month revenue
    return (
        <Shell crumb="Dashboard" active="dashboard">
            <div className="mb-3">
                <div className="font-display text-sm font-semibold tracking-tight">Good to see you, Aditya.</div>
                <div className="text-[10px] text-zinc-500">Your business at a glance — live from Finance Hub &amp; BI.</div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
                <KPI label="Today's sales" value="₹1,84,200" delta="+12.4% vs yesterday" />
                <KPI label="Cash position" value="₹9,42,000" delta="+18.6% MTD" />
                <KPI label="Pending receivables" value="₹8,24,000" positive={false} delta="4 invoices overdue" />
                <KPI label="Low stock alerts" value="46 SKUs" positive={false} delta="6 critical" />
            </div>

            {/* Morning brief + cash flow */}
            <div className="grid grid-cols-5 gap-2">
                <div className="col-span-3 rounded-md border border-zinc-200 bg-white p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="font-display text-[11px] font-semibold">Morning Brief</div>
                        <span className="rounded-full bg-emerald-50 text-emerald-700 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-widest border border-emerald-200">
                            Healthy · 82
                        </span>
                    </div>
                    <div className="space-y-1.5">
                        {[
                            { icon: AlertTriangle, tone: "text-red-600", text: "PVC Pipe 25mm × 6m — 4 days of stock left. Reorder 300 units from Supreme Industries." },
                            { icon: AlertTriangle, tone: "text-amber-600", text: "₹2,84,000 overdue from Sharma Industries (12 days). Draft reminder ready." },
                            { icon: TrendingUp, tone: "text-blue-600", text: "Kohli Hardware placed 3 orders this week — up 40%. Consider volume discount." },
                            { icon: CheckCircle2, tone: "text-emerald-600", text: "PO-2026-1041 from Global Fasteners arriving today 11:30 AM. Rahul assigned." },
                        ].map((r, i) => (
                            <div key={i} className="flex items-start gap-2 text-[10px]">
                                <r.icon className={`h-3 w-3 mt-0.5 ${r.tone}`} strokeWidth={1.5} />
                                <span className="text-zinc-700 leading-snug">{r.text}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="col-span-2 rounded-md border border-zinc-200 bg-white p-3">
                    <div className="font-display text-[11px] font-semibold mb-2">30-day Cash Flow</div>
                    <div className="h-24 relative">
                        <svg viewBox="0 0 100 40" className="w-full h-full" preserveAspectRatio="none">
                            <defs>
                                <linearGradient id="cf-in" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="rgb(16 185 129)" stopOpacity="0.3" />
                                    <stop offset="100%" stopColor="rgb(16 185 129)" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <path
                                d="M0,28 L8,25 L16,22 L24,26 L32,20 L40,18 L48,16 L56,20 L64,14 L72,12 L80,15 L88,10 L96,8 L100,6 L100,40 L0,40 Z"
                                fill="url(#cf-in)"
                            />
                            <path
                                d="M0,28 L8,25 L16,22 L24,26 L32,20 L40,18 L48,16 L56,20 L64,14 L72,12 L80,15 L88,10 L96,8 L100,6"
                                fill="none"
                                stroke="rgb(16 185 129)"
                                strokeWidth="1"
                            />
                            <path
                                d="M0,32 L8,30 L16,33 L24,29 L32,31 L40,28 L48,30 L56,27 L64,29 L72,26 L80,28 L88,25 L96,27 L100,26"
                                fill="none"
                                stroke="rgb(244 63 94)"
                                strokeWidth="1"
                                strokeDasharray="1 1"
                            />
                        </svg>
                    </div>
                    <div className="flex items-center gap-3 text-[9px] text-zinc-500 mt-1">
                        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Inflow</span>
                        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Outflow</span>
                    </div>
                </div>
            </div>
        </Shell>
    );
}

export function ProductBrainPreview() {
    // Building materials wholesale — stock, avg daily sales, days remaining, gross margin, status
    const rows = [
        { sku: "PPL-25MM-6M", name: "PVC Pipe 25mm × 6m ISI", cat: "Plumbing", stock: 84, daily: 19, days: 4, margin: 18.1, status: "critical" },
        { sku: "CEM-OPC-53", name: "OPC 53 Cement 50kg", cat: "Cement", stock: 118, daily: 24, days: 5, margin: 14.2, status: "critical" },
        { sku: "PNT-EMLS-20L", name: "Interior Emulsion 20L", cat: "Paint", stock: 26, daily: 3, days: 8, margin: 26.9, status: "low" },
        { sku: "PLY-19MM-8X4", name: "Waterproof Plywood 19mm", cat: "Wood", stock: 42, daily: 4, days: 11, margin: 22.6, status: "low" },
        { sku: "TMT-12MM-STD", name: "TMT Steel Bar 12mm Fe550", cat: "Steel", stock: 486, daily: 18, days: 27, margin: 11.4, status: "healthy" },
        { sku: "SFB-M10-90", name: "Steel Hex Bolt M10 × 90mm", cat: "Fasteners", stock: 1240, daily: 42, days: 29, margin: 22.4, status: "healthy" },
    ];
    return (
        <Shell crumb="Product Brain" active="product-brain">
            <div className="mb-3 flex items-center justify-between">
                <div>
                    <div className="font-display text-sm font-semibold tracking-tight">Product Brain</div>
                    <div className="text-[10px] text-zinc-500">Every SKU — stock, velocity, margin, and next action.</div>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 py-1 text-[10px]">
                        <Search className="h-2.5 w-2.5 text-zinc-400" strokeWidth={1.5} /> SKU, name, category…
                    </div>
                    <button className="rounded-md bg-zinc-900 text-white px-2.5 py-1 text-[10px] font-medium">+ Add Product</button>
                </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
                <KPI label="Active SKUs" value="1,982" />
                <KPI label="Critical" value="6" positive={false} delta="reorder now" />
                <KPI label="Low stock" value="46" positive={false} delta="est. 4-11 days" />
                <KPI label="Avg gross margin" value="19.6%" delta="+0.8pt MTD" />
            </div>
            <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                <div className="grid grid-cols-12 gap-2 border-b border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-widest text-zinc-500">
                    <div className="col-span-2">SKU</div>
                    <div className="col-span-4">Product</div>
                    <div className="col-span-2">Category</div>
                    <div className="col-span-1 text-right">Stock</div>
                    <div className="col-span-1 text-right">Days left</div>
                    <div className="col-span-2 text-right">Margin / Status</div>
                </div>
                {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 border-b border-zinc-100 px-2.5 py-1.5 text-[10.5px]">
                        <div className="col-span-2 font-mono text-zinc-600">{r.sku}</div>
                        <div className="col-span-4 truncate">{r.name}</div>
                        <div className="col-span-2 text-zinc-500">{r.cat}</div>
                        <div className={`col-span-1 text-right font-medium ${r.status === "critical" ? "text-red-600" : r.status === "low" ? "text-amber-600" : "text-zinc-900"}`}>{r.stock}</div>
                        <div className={`col-span-1 text-right font-medium ${r.status === "critical" ? "text-red-600" : r.status === "low" ? "text-amber-600" : "text-zinc-700"}`}>{r.days}d</div>
                        <div className="col-span-2 text-right font-medium">
                            <span className="text-zinc-900">{r.margin}%</span>
                            <span
                                className={`ml-1.5 inline-block rounded px-1 py-px text-[8px] font-semibold uppercase tracking-wider ${
                                    r.status === "critical"
                                        ? "bg-red-50 text-red-700 border border-red-200"
                                        : r.status === "low"
                                        ? "bg-amber-50 text-amber-700 border border-amber-200"
                                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                }`}
                            >
                                {r.status}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </Shell>
    );
}

export function InventoryPreview() {
    // Each row explains WHY action is needed — velocity, predicted stockout, supplier, recommended reorder
    const alerts = [
        { sku: "PPL-25MM-6M", name: "PVC Pipe 25mm × 6m", sup: "Supreme Industries · ₹186/unit", qty: 84, reorder: 300, days: 4, daily: 19 },
        { sku: "CEM-OPC-53", name: "OPC 53 Cement 50kg", sup: "Anand Cement Traders · ₹412/bag", qty: 118, reorder: 400, days: 5, daily: 24 },
        { sku: "PNT-EMLS-20L", name: "Interior Emulsion 20L", sup: "Ashok Paints · ₹2,840/pail", qty: 26, reorder: 80, days: 8, daily: 3 },
        { sku: "PLY-19MM-8X4", name: "Waterproof Plywood 19mm", sup: "Ambika Wood Traders · ₹1,240/sheet", qty: 42, reorder: 120, days: 11, daily: 4 },
    ];
    return (
        <Shell crumb="Inventory Intelligence" active="inventory-intelligence">
            <div className="mb-3">
                <div className="font-display text-sm font-semibold tracking-tight">Inventory Intelligence</div>
                <div className="text-[10px] text-zinc-500">Predicts stockouts before they happen — with the reorder quantity and preferred supplier.</div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
                <KPI label="Inventory value" value="₹2.14 Cr" />
                <KPI label="Units on hand" value="48,290" />
                <KPI label="Predicted stockouts" value="6 SKUs" positive={false} delta="in next 10 days" />
                <KPI label="Recommended POs" value="₹4.86 L" delta="4 suppliers · 1 tap" />
            </div>
            <div className="rounded-md border border-zinc-200 bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="font-display text-[11px] font-semibold">Predicted stockout — next 10 days</div>
                    <span className="text-[9px] text-zinc-500">Based on 30-day velocity</span>
                </div>
                {alerts.map((a, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center py-1.5 border-b border-zinc-100 last:border-0 text-[10.5px]">
                        <div className="col-span-2 font-mono text-zinc-600">{a.sku}</div>
                        <div className="col-span-4 truncate">
                            <div className="truncate">{a.name}</div>
                            <div className="text-[9px] text-zinc-500 truncate">{a.sup} · sells {a.daily}/day</div>
                        </div>
                        <div className="col-span-3">
                            <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${a.qty / a.reorder < 0.5 ? "bg-red-500" : "bg-amber-500"}`}
                                        style={{ width: `${Math.min((a.qty / a.reorder) * 100, 100)}%` }}
                                    />
                                </div>
                                <span className="text-[9.5px] text-zinc-500 font-mono w-16 text-right">
                                    {a.qty}/{a.reorder}
                                </span>
                            </div>
                        </div>
                        <div className="col-span-2 text-right text-red-600 font-medium">~{a.days} days</div>
                        <div className="col-span-1 text-right">
                            <button className="rounded bg-zinc-900 text-white px-1.5 py-0.5 text-[9px]">Reorder</button>
                        </div>
                    </div>
                ))}
            </div>
        </Shell>
    );
}

export function WarehousePreview() {
    // Bins tied to real products across the catalog — connected to Inventory + Procurement stories
    const bins = [
        { code: "A-01-01", usage: 62, cap: 100, sku: "OPC 53 Cement" },
        { code: "A-01-02", usage: 42, cap: 100, sku: "PVC Pipe 25mm" },
        { code: "A-02-04", usage: 96, cap: 100, sku: "Red Clay Brick" },
        { code: "B-01-01", usage: 84, cap: 100, sku: "Steel Hex Bolt" },
        { code: "B-02-03", usage: 71, cap: 100, sku: "Plywood 19mm" },
        { code: "C-01-01", usage: 18, cap: 80, sku: "Emulsion 20L" },
        { code: "C-02-02", usage: 48, cap: 100, sku: "Ceramic Tile 600" },
        { code: "D-01-01", usage: 34, cap: 60, sku: "PVC Conduit" },
    ];
    return (
        <Shell crumb="Warehouse Hub" active="warehouse-hub">
            <div className="mb-3 flex items-center justify-between">
                <div>
                    <div className="font-display text-sm font-semibold tracking-tight">Warehouse Hub</div>
                    <div className="text-[10px] text-zinc-500">Bin-level visibility, guided receiving, and dispatch operations.</div>
                </div>
                <button className="rounded-md bg-zinc-900 text-white px-2.5 py-1 text-[10px] font-medium">
                    Start Guided Receive
                </button>
            </div>
            <div className="grid grid-cols-6 gap-2 mb-3">
                {bins.map((b) => (
                    <div key={b.code} className="rounded-md border border-zinc-200 bg-white p-2">
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-[10px] font-semibold text-zinc-900">{b.code}</span>
                            <span className="text-[8px] text-zinc-500">{b.usage}/{b.cap}</span>
                        </div>
                        <div className="mt-1 h-1 bg-zinc-100 rounded-full overflow-hidden">
                            <div
                                className={`h-full ${b.usage / b.cap > 0.9 ? "bg-red-500" : b.usage / b.cap > 0.7 ? "bg-amber-500" : "bg-emerald-500"}`}
                                style={{ width: `${(b.usage / b.cap) * 100}%` }}
                            />
                        </div>
                        <div className="mt-1 truncate text-[9px] text-zinc-500">{b.sku}</div>
                    </div>
                ))}
            </div>
            <div className="rounded-md border border-zinc-200 bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="font-display text-[11px] font-semibold">Receiving Queue — today</div>
                    <span className="text-[9px] text-zinc-500">3 POs · 35 items</span>
                </div>
                <div className="space-y-1.5">
                    {[
                        { po: "PO-2026-1041", sup: "Supreme Industries", items: 12, eta: "11:30 AM", staff: "Rahul", status: "Ready to receive", tone: "emerald" },
                        { po: "PO-2026-1042", sup: "Anand Cement Traders", items: 8, eta: "2:15 PM", staff: "Naveen", status: "In transit", tone: "amber" },
                        { po: "PO-2026-1043", sup: "Ashok Paints & Coatings", items: 15, eta: "4:00 PM", staff: "Amit", status: "Scheduled", tone: "zinc" },
                    ].map((p) => (
                        <div key={p.po} className="flex items-center gap-2 text-[10.5px]">
                            <span className="font-mono text-zinc-600 w-24">{p.po}</span>
                            <span className="flex-1 truncate">{p.sup}</span>
                            <span className="text-zinc-500 w-14">{p.items} items</span>
                            <span className="font-mono text-zinc-700 w-16 text-right">{p.eta}</span>
                            <span className="text-zinc-500 w-14 truncate">{p.staff}</span>
                            <span
                                className={`rounded px-1.5 py-0.5 text-[9px] font-medium border ${
                                    p.tone === "emerald"
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                        : p.tone === "amber"
                                        ? "bg-amber-50 text-amber-700 border-amber-200"
                                        : "bg-zinc-50 text-zinc-600 border-zinc-200"
                                }`}
                            >
                                {p.status}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </Shell>
    );
}

export function CustomerBrainPreview() {
    const rows = [
        { name: "Kohli Hardware Stores", city: "Ludhiana", ltv: "18,42,000", orders: 84, last: "2 days ago", out: 0 },
        { name: "Sharma Industries", city: "Delhi", ltv: "14,20,600", orders: 62, last: "12 days ago", out: 284000 },
        { name: "Verma Builders LLP", city: "Chandigarh", ltv: "12,84,500", orders: 71, last: "1 day ago", out: 42000 },
        { name: "Prakash Enterprises", city: "Amritsar", ltv: "9,60,200", orders: 48, last: "5 days ago", out: 0 },
        { name: "Bansal Sanitary House", city: "Jalandhar", ltv: "8,12,900", orders: 39, last: "3 days ago", out: 68000 },
    ];
    return (
        <Shell crumb="Customer Brain" active="customer-brain">
            <div className="mb-3">
                <div className="font-display text-sm font-semibold tracking-tight">Customer Brain</div>
                <div className="text-[10px] text-zinc-500">Buying patterns, lifetime value, and relationship intelligence.</div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
                <KPI label="Active customers" value="248" />
                <KPI label="Revenue MTD" value="₹42.6 L" delta="+18.4%" />
                <KPI label="Receivables" value="₹8.24 L" positive={false} delta="4 overdue" />
                <KPI label="At-risk" value="6" positive={false} delta=">60 days silent" />
            </div>
            <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                <div className="grid grid-cols-12 gap-2 border-b border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-widest text-zinc-500">
                    <div className="col-span-3">Customer</div>
                    <div className="col-span-2">City</div>
                    <div className="col-span-2 text-right">Lifetime value</div>
                    <div className="col-span-1 text-right">Orders</div>
                    <div className="col-span-2">Last order</div>
                    <div className="col-span-2 text-right">Outstanding</div>
                </div>
                {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 border-b border-zinc-100 px-2.5 py-1.5 text-[10.5px]">
                        <div className="col-span-3 font-medium">{r.name}</div>
                        <div className="col-span-2 text-zinc-500">{r.city}</div>
                        <div className="col-span-2 text-right font-medium">₹{r.ltv}</div>
                        <div className="col-span-1 text-right text-zinc-700">{r.orders}</div>
                        <div className="col-span-2 text-zinc-500">{r.last}</div>
                        <div className={`col-span-2 text-right ${r.out > 0 ? "text-red-600 font-medium" : "text-zinc-400"}`}>
                            {r.out > 0 ? `₹${r.out.toLocaleString("en-IN")}` : "—"}
                        </div>
                    </div>
                ))}
            </div>
        </Shell>
    );
}

export function SupplierBrainPreview() {
    const rows = [
        { name: "Supreme Industries", cat: "Plumbing · PVC", spend: "12,84,000", lead: 4, onTime: 98 },
        { name: "Global Fasteners Pvt Ltd", cat: "Steel Fittings", spend: "8,42,600", lead: 6, onTime: 96 },
        { name: "Ambika Steel Industries", cat: "TMT · Structural Steel", spend: "7,20,400", lead: 5, onTime: 88 },
        { name: "Anand Cement Traders", cat: "Cement · OPC/PPC", spend: "6,86,000", lead: 3, onTime: 92 },
        { name: "Ashok Paints & Coatings", cat: "Paint · Emulsion", spend: "4,20,400", lead: 9, onTime: 74 },
    ];
    return (
        <Shell crumb="Supplier Brain" active="supplier-brain">
            <div className="mb-3">
                <div className="font-display text-sm font-semibold tracking-tight">Supplier Brain</div>
                <div className="text-[10px] text-zinc-500">Reliability scored, per supplier — lead times and negotiation intelligence.</div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
                <KPI label="Active suppliers" value="42" />
                <KPI label="Total spend (30d)" value="₹1.28 Cr" />
                <KPI label="Avg lead time" value="5.4 days" />
                <KPI label="Payables" value="₹6.42 L" positive={false} delta="₹2.1L overdue" />
            </div>
            <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                <div className="grid grid-cols-12 gap-2 border-b border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-widest text-zinc-500">
                    <div className="col-span-4">Supplier</div>
                    <div className="col-span-3">Category</div>
                    <div className="col-span-2 text-right">Spend</div>
                    <div className="col-span-1 text-right">Lead</div>
                    <div className="col-span-2 text-right">On-time %</div>
                </div>
                {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 border-b border-zinc-100 px-2.5 py-1.5 text-[10.5px]">
                        <div className="col-span-4 font-medium">{r.name}</div>
                        <div className="col-span-3 text-zinc-500">{r.cat}</div>
                        <div className="col-span-2 text-right font-medium">₹{r.spend}</div>
                        <div className="col-span-1 text-right text-zinc-700">{r.lead}d</div>
                        <div className="col-span-2 text-right">
                            <span
                                className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                                    r.onTime >= 90
                                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                        : r.onTime >= 80
                                        ? "bg-amber-50 text-amber-700 border border-amber-200"
                                        : "bg-red-50 text-red-700 border border-red-200"
                                }`}
                            >
                                {r.onTime}%
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </Shell>
    );
}

export function FinanceHubPreview() {
    return (
        <Shell crumb="Finance Hub" active="finance-hub">
            <div className="mb-3">
                <div className="font-display text-sm font-semibold tracking-tight">Finance Hub</div>
                <div className="text-[10px] text-zinc-500">Cashflow, ledgers, and the financial nervous system.</div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
                <KPI label="Cash in (today)" value="₹1,52,800" delta="+8.1%" />
                <KPI label="Expenses (today)" value="₹34,600" positive={false} delta="−4.2%" />
                <KPI label="Est. profit" value="₹41,900" delta="+18.6%" />
                <KPI label="Receivables" value="₹8.24 L" positive={false} delta="4 overdue" />
            </div>
            <div className="grid grid-cols-5 gap-2">
                <div className="col-span-3 rounded-md border border-zinc-200 bg-white p-3">
                    <div className="font-display text-[11px] font-semibold mb-2">Cash-in by method — today</div>
                    <div className="space-y-1.5">
                        {[
                            { m: "UPI", v: 84200, pct: 55 },
                            { m: "Bank Transfer", v: 42600, pct: 28 },
                            { m: "Cash", v: 18400, pct: 12 },
                            { m: "Card", v: 7600, pct: 5 },
                        ].map((r) => (
                            <div key={r.m} className="flex items-center gap-2 text-[10.5px]">
                                <span className="w-24 text-zinc-600">{r.m}</span>
                                <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-zinc-900" style={{ width: `${r.pct}%` }} />
                                </div>
                                <span className="w-24 text-right font-medium">₹{r.v.toLocaleString("en-IN")}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="col-span-2 rounded-md border border-zinc-200 bg-white p-3">
                    <div className="font-display text-[11px] font-semibold mb-2">Expense breakdown</div>
                    <div className="space-y-1.5 text-[10.5px]">
                        {[
                            { c: "Freight", v: 12400 },
                            { c: "Labour", v: 8600 },
                            { c: "Utilities", v: 6200 },
                            { c: "Fuel", v: 4800 },
                            { c: "Miscellaneous", v: 2600 },
                        ].map((r) => (
                            <div key={r.c} className="flex justify-between">
                                <span className="text-zinc-600">{r.c}</span>
                                <span className="font-medium">₹{r.v.toLocaleString("en-IN")}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Shell>
    );
}

export function BIPreview() {
    return (
        <Shell crumb="Business Intelligence" active="business-intelligence">
            <div className="mb-3 flex items-center justify-between">
                <div>
                    <div className="font-display text-sm font-semibold tracking-tight">Business Intelligence</div>
                    <div className="text-[10px] text-zinc-500">Composable analytics — filter across the entire business.</div>
                </div>
                <div className="flex items-center gap-1.5 text-[9px]">
                    {["Executive", "Sales", "Products", "Customers", "Suppliers", "Financial"].map((t, i) => (
                        <span
                            key={t}
                            className={`rounded px-1.5 py-0.5 border ${
                                i === 0
                                    ? "bg-zinc-900 text-white border-zinc-900"
                                    : "bg-white border-zinc-200 text-zinc-600"
                            }`}
                        >
                            {t}
                        </span>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
                <KPI label="Revenue (30d)" value="₹42.68 L" delta="+18.4% vs prev period" />
                <KPI label="Gross margin" value="21.4%" delta="+1.2pt" />
                <KPI label="Cash net" value="₹9.42 L" delta="+22.1%" />
                <KPI label="Overdue" value="₹2.84 L" positive={false} delta="4 invoices" />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md border border-zinc-200 bg-white p-3">
                    <div className="font-display text-[11px] font-semibold mb-1">Top products by revenue (30d)</div>
                    <div className="space-y-1 text-[10.5px]">
                        {[
                            { n: "PVC Pipe 25mm × 6m ISI", v: 682400 },
                            { n: "OPC 53 Cement 50kg", v: 542600 },
                            { n: "TMT Steel Bar 12mm Fe550", v: 486000 },
                            { n: "Steel Hex Bolt M10 × 90mm", v: 342000 },
                            { n: "Interior Emulsion 20L", v: 284200 },
                        ].map((r, i) => (
                            <div key={r.n} className="flex items-center gap-2">
                                <span className="w-4 text-zinc-400 font-mono">{i + 1}</span>
                                <span className="flex-1 truncate">{r.n}</span>
                                <span className="font-medium">₹{r.v.toLocaleString("en-IN")}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="rounded-md border border-zinc-200 bg-white p-3">
                    <div className="font-display text-[11px] font-semibold mb-2">Revenue — daily</div>
                    <div className="h-20 relative">
                        <svg viewBox="0 0 100 40" className="w-full h-full" preserveAspectRatio="none">
                            {[8, 12, 10, 14, 11, 18, 16, 20, 14, 24, 22, 26, 20, 28].map((h, i, arr) => (
                                <rect
                                    key={i}
                                    x={i * (100 / arr.length) + 0.6}
                                    y={40 - h}
                                    width={100 / arr.length - 1.2}
                                    height={h}
                                    fill="rgb(24 24 27)"
                                    opacity={0.6 + (i / arr.length) * 0.4}
                                />
                            ))}
                        </svg>
                    </div>
                    <div className="flex items-center justify-between text-[9px] text-zinc-500 mt-1">
                        <span>Jan 15</span>
                        <span>Jan 28</span>
                    </div>
                </div>
            </div>
        </Shell>
    );
}

export function YugBrainPreview() {
    return (
        <Shell crumb="YugOS Intelligence" active="yug-brain">
            <div className="mb-3 flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-500 text-white">
                    <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                </div>
                <div>
                    <div className="font-display text-sm font-semibold tracking-tight flex items-center gap-1.5">
                        YugOS Intelligence
                        <span className="text-[8px] font-semibold tracking-widest uppercase text-blue-500 border border-blue-200 bg-blue-50 rounded px-1 py-0.5">
                            AI
                        </span>
                    </div>
                    <div className="text-[10px] text-zinc-500">Ask anything about your business — powered by live data.</div>
                </div>
            </div>

            <div className="flex flex-col h-[calc(100%-3rem)]">
                <div className="flex-1 overflow-y-auto space-y-3">
                    <div className="flex justify-end">
                        <div className="max-w-[70%] rounded-2xl rounded-tr-sm bg-zinc-900 text-white px-3 py-2 text-[11px]">
                            Which customers haven&apos;t ordered in the last 60 days but owe us money?
                        </div>
                    </div>
                    <div className="flex justify-start">
                        <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white border border-zinc-200 px-3 py-2.5 text-[11px] space-y-1.5">
                            <div className="text-zinc-800 leading-relaxed">
                                3 customers match — combined outstanding <span className="font-semibold">₹4,84,000</span>:
                            </div>
                            <div className="rounded-md border border-zinc-200 divide-y divide-zinc-100 text-[10.5px]">
                                {[
                                    { c: "Sharma Industries", days: 78, out: "₹2,84,000" },
                                    { c: "Bansal Traders", days: 92, out: "₹1,24,000" },
                                    { c: "Ravi Enterprises", days: 104, out: "₹76,000" },
                                ].map((r) => (
                                    <div key={r.c} className="grid grid-cols-4 gap-2 px-2 py-1.5">
                                        <span className="col-span-2 font-medium">{r.c}</span>
                                        <span className="text-zinc-500">{r.days} days silent</span>
                                        <span className="text-right text-red-600 font-medium">{r.out}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="text-zinc-600 text-[10px] leading-relaxed">
                                Suggested action: send a soft-touch reminder to Sharma Industries first — highest outstanding, most recent contact.
                            </div>
                            <div className="flex gap-1.5 pt-1">
                                <button className="rounded bg-zinc-900 text-white px-2 py-0.5 text-[9.5px] font-medium">
                                    Open Customer Brain
                                </button>
                                <button className="rounded border border-zinc-200 bg-white px-2 py-0.5 text-[9.5px]">
                                    Draft reminder
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 flex items-center gap-2">
                    <Sparkles className="h-3 w-3 text-blue-500" strokeWidth={1.5} />
                    <input
                        className="flex-1 bg-transparent text-[11px] outline-none placeholder:text-zinc-400"
                        placeholder="Ask YugOS Intelligence anything about your business…"
                        readOnly
                        value=""
                    />
                    <span className="font-mono text-[9px] text-zinc-400">↵</span>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {["When will PVC Pipe 25mm stock out?", "Draft reminder for Sharma Industries", "Top 5 customers this month"].map((s) => (
                        <span key={s} className="rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-[9.5px] text-zinc-600">
                            {s}
                        </span>
                    ))}
                </div>
            </div>
        </Shell>
    );
}

/** ProductPreviewCarousel — shows one preview at a time based on active tab. */
export const PREVIEWS = {
    dashboard: { label: "Dashboard", component: DashboardPreview, icon: LayoutDashboard },
    "product-brain": { label: "Product Brain", component: ProductBrainPreview, icon: Boxes },
    "inventory-intelligence": { label: "Inventory", component: InventoryPreview, icon: LineChart },
    "warehouse-hub": { label: "Warehouse Hub", component: WarehousePreview, icon: Warehouse },
    "customer-brain": { label: "Customer Brain", component: CustomerBrainPreview, icon: Users },
    "supplier-brain": { label: "Supplier Brain", component: SupplierBrainPreview, icon: Truck },
    "finance-hub": { label: "Finance Hub", component: FinanceHubPreview, icon: Wallet },
    "business-intelligence": { label: "Business Intelligence", component: BIPreview, icon: BarChart3 },
    "yug-brain": { label: "YugOS Intelligence", component: YugBrainPreview, icon: Sparkles, ai: true },
};

export function ArrowLink() {
    return <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />;
}
